import { library } from '@fortawesome/fontawesome-svg-core';
import { faCopy, faEdit, faStopCircle, faSync } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { EditIcon } from '../../../../design/static/icons/EditIcon';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { EditApiKeyModalContainer } from '../containers/EditApiKeyModal.container';
import { IApiKey } from '../integrations.store';

library.add(faEdit, faSync, faStopCircle, faCopy);

interface IApiKeyListItemViewModel {
  id: string;
  name: string;
  value: string;
  isActiveText: string;
  copiedClassName: 'success' | 'primary';
}

interface IIntegrationsListComponentProps {
  apiKeys: IApiKey[];
  isLoading: boolean;
  onDeleteApiKey(apiKeyId: string): void;
}

export const IntegrationsList = observer((props: IIntegrationsListComponentProps): ReactElement => {
  const { t } = useTranslator();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [apiKeyId, setApiKeyId] = useState<string | undefined>(undefined);
  const [copiedId, setCopiedId] = useState<string | undefined>(undefined);

  const showModalHandler = (id?: string): void => {
    setShowModal(true);
    setApiKeyId(id);
  };

  const hideModalHandler = (): void => {
    setShowModal(false);
    setApiKeyId(undefined);
  };

  const handleCopied = (id?: string): void => {
    setCopiedId(id);
  };

  const renderButtonsAndIcons = (params: IApiKeyListItemViewModel): ReactElement => {
    return (
      <>
        <CopyToClipboard
          text={params.value}
          onCopy={(): void => {
            handleCopied(params.id);
          }}
        >
          <IconButton
            size='sm'
            color={params.copiedClassName}
            title={t('copy_to_clipboard')}
          >
            <FontAwesomeIcon icon={faCopy} />
          </IconButton>
        </CopyToClipboard>
        <IconButton
          size='sm'
          color='primary'
          onClick={(): void => {
            showModalHandler(params.id);
          }}
          title={t('edit_api_key')}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size='sm'
          onClick={(): void => {
            props.onDeleteApiKey(params.id);
          }}
          title={t('delete_api_key')}
        >
          <TrashIcon />
        </IconButton>
      </>
    );
  };

  const columns: IGColumnProps<IApiKeyListItemViewModel>[] = [
    {
      sortBy: 'name',
      headerName: t('name'),
      renderer: (x) => x.name,
    },
    {
      sortBy: 'isActiveText',
      headerName: t('status'),
      renderer: (x) => x.isActiveText,
    },
    {
      sortBy: 'value',
      headerName: t('value'),
      renderer: (x) => x.value,
    },
    {
      headerName: t('actions'),
      renderer: (data) => renderButtonsAndIcons(data),
      actions: true,
      width: 'min-content',
    },
  ];

  const apiKeys: IApiKeyListItemViewModel[] = props.apiKeys.map((element) => ({
    id: element.id,
    name: element.name,
    value: element.value,
    isActiveText: t(element.isActive ? 'active' : 'inactive'),
    copiedClassName: element.id === copiedId ? 'success' : 'primary',
  }));

  return (
    <div>
      <GTable<IApiKeyListItemViewModel>
        isLoading={props.isLoading}
        columns={columns}
        items={apiKeys}
        noItemsPlaceholder={t('there_are_no_records_to_display')}
      />
      {apiKeyId && (
        <EditApiKeyModalContainer
          apiKeyId={apiKeyId}
          onSubmit={hideModalHandler}
          onCancel={hideModalHandler}
          showModal={showModal}
        />
      )}
    </div>
  );
});
